"use client"

import type { ReactNode } from "react"
import { Form, Formik, type FormikErrors } from "formik"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FormInput } from "@/components/form-inputs"
import { useUpdateTenantNotificationPolicyMutation } from "@/hooks/mutations/tenant-notification-policies"
import type { TenantNotificationView } from "@/server-actions/tenant-notification-policies"
import { NotificationRow } from "./notification-row"
import { useTranslations } from "next-intl"



interface TenantNotificationValues {
  enabled: boolean
  parameters: Record<string, number>
}

interface TenantNotificationRowProps {
  notification: TenantNotificationView
  /** A setting kept outside the policy but tuned here, such as the default low-balance threshold. */
  extra?: ReactNode
}

export function TenantNotificationRow({ notification, extra }: TenantNotificationRowProps) {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsTenantNotificationRow")

  const { mandatory, enabled } = notification
  // Per row: a shared mutation tracks only the latest save and re-enables a row still saving.
  const update = useUpdateTenantNotificationPolicyMutation()
  const saving = update.isPending
  const onSave = (values: TenantNotificationValues) =>
    update.mutateAsync({ eventType: notification.eventType, ...values }).catch(() => {})
  const savedParameters = Object.fromEntries(notification.parameters.map((parameter) => [parameter.key, parameter.value]))

  return (
    <NotificationRow
      label={notification.label}
      description={notification.description}
      badges={
        <>
          {mandatory && <Badge variant="secondary"><span key={tUi("alwaysSent")}>{tUi("alwaysSent")}</span></Badge>}
          {!enabled && <Badge variant="outline"><span key={tUi("off")}>{tUi("off")}</span></Badge>}
        </>
      }
      channelTypes={notification.channelTypes}
      toggle={{
        label: `Send "${notification.label}" to tenants`,
        enabled,
        disabled: saving || mandatory,
        onChange: (next) => void onSave({ enabled: next, parameters: savedParameters }),
      }}
    >
      {notification.parameters.length > 0 && (
        <ParametersForm notification={notification} savedParameters={savedParameters} onSave={onSave} saving={saving} />
      )}
      {extra}
    </NotificationRow>
  )
}

function ParametersForm({
  notification,
  savedParameters,
  onSave,
  saving,
}: {
  notification: TenantNotificationView
  savedParameters: Record<string, number>
  onSave: (values: TenantNotificationValues) => Promise<unknown>
  saving: boolean
}) {

  const tUi = useTranslations("SystemNotifications.SystemNotificationsTenantNotificationRow")

  return (
    <Formik<{ parameters: Record<string, number> }>
      enableReinitialize
      initialValues={{ parameters: savedParameters }}
      validate={(values) => validateParameters(notification, values.parameters)}
      onSubmit={(values) => onSave({ enabled: notification.enabled, parameters: values.parameters })}
    >
      {({ dirty, isSubmitting }) => (
        // Range errors come from Formik, not the browser's own validation.
        <Form noValidate className="space-y-2">
          {notification.parameters.map((parameter) => (
            <FormInput
              key={parameter.key}
              name={`parameters.${parameter.key}`}
              label={parameter.label}
              description={tUi("value0Value1Value2", { value0: String(parameter.description), value1: String(parameter.min), value2: String(parameter.max) })}
              type="number"
              min={parameter.min}
              max={parameter.max}
              step={1}
              containerClassName="max-w-[16rem]"
              disabled={saving}
            />
          ))}
          {dirty && (
            <Button type="submit" size="sm" disabled={saving || isSubmitting}>
              <span key={tUi("save")}>{tUi("save")}</span></Button>
          )}
        </Form>
      )}
    </Formik>
  )
}

function validateParameters(
  { parameters }: TenantNotificationView,
  values: Record<string, number>,
): FormikErrors<{ parameters: Record<string, number> }> {
  const errors: Record<string, string> = {}
  for (const parameter of parameters) {
    const value = values[parameter.key]
    if (!Number.isInteger(value) || value < parameter.min || value > parameter.max) {
      errors[parameter.key] = `Enter a whole number from ${parameter.min} to ${parameter.max}.`
    }
  }
  return Object.keys(errors).length > 0 ? { parameters: errors } : {}
}
